import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Layout, message, Table, TableProps } from 'antd';
import { DeleteOutlined, EditOutlined, } from '@ant-design/icons';
import type { ColumnsType, SorterResult } from 'antd/es/table/interface';
import { IChannelEntity, IItemsWithCount, KeyValueRecord, transformToFormattedTime } from '@rasayi-workspace/shared';
import { MapAntDesignSearchFilter, MapAntDesignSortOrder, RemoveUndefinedKeyPairs } from '@helpers';
import { BASE_QUERY_OPTIONS, DEFAULT_TABLE_STATE } from '@constants';
import { DeleteItem, EMPTY_INITIAL_ITEMS, GetTableItems } from '@services';
import { BaseLayoutComponent, ColumnsSearchProps, DeleteConfirmationModal, TablePageTitleComponent } from '@components';
import { ErrorResponse, ITableState } from '@interfaces';
import { ChannelModalComponent } from './add-edit';

const { Content } = Layout;

const ChannelPageComponent = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [tableState, setTableState] = useState<ITableState>(DEFAULT_TABLE_STATE);
    const [channelsData, setChannelsData] = useState<IItemsWithCount<IChannelEntity>>(EMPTY_INITIAL_ITEMS);
    const [isChannelModalOpened, setIsChannelModalOpened] = useState(false);
    const [editingChannelId, setEditingChannelId] = useState('');

    const { refetch, isFetching } = useQuery<IItemsWithCount<IChannelEntity>, AxiosError>({
        ...BASE_QUERY_OPTIONS,
        queryKey: ['channels'],
        queryFn: () => GetTableItems<IChannelEntity>(
            'channel',
            [],
            [],
            tableState
        ),
        onSuccess: (returnedResult: IItemsWithCount<IChannelEntity>): void => setChannelsData(returnedResult),
        onError: () => setChannelsData(EMPTY_INITIAL_ITEMS)
    });

    const { mutate: deleteChannel } = useMutation<IChannelEntity, AxiosError>({
        mutationKey: ['deleteChannel'],
        mutationFn: async () =>
            DeleteItem<IChannelEntity>(
                'channel',
                editingChannelId,
            ),
        onSuccess: async (): Promise<void> => {
            messageApi.open({
                type: 'success',
                content: `Channel deleted!`,
            });

            refetch();
            resetModal();
        },
        onError: (error: AxiosError) => {
            messageApi.open({
                type: 'error',
                content: (error?.response?.data as ErrorResponse)?.message || 'Contact support for details'
            });
        }
    });

    const handleChange: TableProps<IChannelEntity>['onChange'] = (pagination, filters, sorter) => {
        let sortBy: KeyValueRecord = RemoveUndefinedKeyPairs({
            [(sorter as SorterResult<IChannelEntity>).columnKey as keyof IChannelEntity]: MapAntDesignSortOrder((sorter as SorterResult<IChannelEntity>).order)
        });

        sortBy = Object.keys(sortBy).length ? sortBy : DEFAULT_TABLE_STATE.sortBy;

        setTableState({
            ...tableState,
            page: pagination.current || tableState.page,
            pageSize: pagination.pageSize || tableState.pageSize,
            sortBy: sortBy,
            search: MapAntDesignSearchFilter(RemoveUndefinedKeyPairs(filters))
        });
    };

    const onChannelModalCloseHandler = (newChannel: IChannelEntity) => {
        refetch();
        resetModal();

        messageApi.open(
            !editingChannelId ? {
                type: 'success',
                content: `New channel '${ newChannel?.name }' created!`,
            } : {
                type: 'success',
                content: `Channel '${ newChannel?.name }' updated!`,
            }
        );
    };

    const onModalCloseHandler = () => resetModal();

    const resetModal = () => {
        setIsChannelModalOpened(false);
        setEditingChannelId('');
    };

    const resetSearchHandler = (dataIndex: string) => {
        setTableState({
            ...tableState,
            search: tableState.search?.filter((item) => !item.dataKey) || []
        });
    };

    const columns: ColumnsType<IChannelEntity> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('name', 'by name', resetSearchHandler)
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('description', 'by description', resetSearchHandler)
        },
        {
            title: 'Created at',
            dataIndex: 'created_at',
            key: 'created_at',
            ellipsis: true,
            render: (value: string) => transformToFormattedTime(value),
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Last updated at',
            dataIndex: 'updated_at',
            key: 'updated_at',
            ellipsis: true,
            render: (value: string) => transformToFormattedTime(value),
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Actions',
            dataIndex: 'operation',
            key: 'operation',
            render: (_, { id, name = '' }) => (
                <div className='lg:flex'>
                    <Button
                        type='text'
                        shape='circle'
                        icon={ <EditOutlined/> }
                        // disabled={!HasAnyPermission([PermissionsEnum.USER_FULL, PermissionsEnum.USER_WRITE])}
                        onClick={
                            () => {
                                setEditingChannelId(id);
                                setIsChannelModalOpened(true);
                            }
                        }
                    />
                    <Button
                        className='ml-2'
                        type='text'
                        shape='circle'
                        icon={ <DeleteOutlined/> }
                        // disabled={!HasAnyPermission([PermissionsEnum.USER_FULL, PermissionsEnum.USER_DELETE])}
                        onClick={
                            () => {
                                setEditingChannelId(id);

                                DeleteConfirmationModal({
                                    content: `Are you sure you want to delete channel '${ name }'?`,
                                    onOk: () => {
                                        deleteChannel();
                                    },
                                    onCancel: () => resetModal()

                                });
                            }
                        }
                    />
                </div>
            ),
        }
    ];

    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState]);

    return (
        <>
            { contextHolder }
            {
                isChannelModalOpened &&
                <ChannelModalComponent
                    editMode={ !!editingChannelId }
                    id={ editingChannelId }
                    onClose={ onModalCloseHandler }
                    onSubmit={ onChannelModalCloseHandler }
                />
            }
            <BaseLayoutComponent children={
                <Content className='m-5 bg-white border rounded-lg'>
                    <Card
                        title={
                            <TablePageTitleComponent
                                title='Channels'
                                addButtonText='Add Channel'
                                // disabled={!HasAnyPermission([PermissionsEnum.USER_FULL, PermissionsEnum.USER_WRITE])}
                                onClick={ () => setIsChannelModalOpened(true) }
                            />
                        }
                    >
                        <div className='border-2 border-black rounded-lg'>
                            <Table
                                columns={ columns }
                                dataSource={ channelsData.items }
                                onChange={ handleChange }
                                pagination={
                                    {
                                        total: channelsData.count,
                                        pageSize: channelsData.pageSize,
                                        current: channelsData.page,
                                    }
                                }
                                loading={ isFetching }
                                rowKey={ 'id' }
                                size='small'
                                scroll={ { x: true } }
                            />
                        </div>
                    </Card>
                </Content>
            }
            />
        </>
    );
};

export default ChannelPageComponent;